// taxonomy.ts · v12.10.29 (#2237 shortRequestType - the type drops a word repeated from the area) · 2026-11-03 · VoiceSpectrum AI
// taxonomy.ts · v1.0 · v12.10.17 (#1723 T120 / BDR-018 D2 D6 D10 D12) · 2026-10-16 · VoiceSpectrum AI
//
// ONE resolver for what a message record stores as its caller type and category. From 12.10.17 the record stores the
// workflow KEY (the workflow id, the intent id - BDR-001 tokens); records written before stored the NAME. The card, drawer,
// filters and the call-record print look the name, colour and icon up here at display time: KEY first, then NAME, then an
// ALIAS (the names / keys GET /t2/surface/personalization says an old record may carry for the item), so a workflow rename
// reaches old and new cards and old records render without the data script.
//
// Nothing resolved = the system type "Unclassified" (plain person / question mark on slate #64748B) - never Patient (D2),
// never "General" (D10). Not tenant-editable; slate is never offered as a tenant colour.

export interface TaxonomyItem { key: string; name: string; color?: string; icon?: string; active?: boolean; aliases?: string[] }

export const UNCLASSIFIED_KEY = 'unclassified';
export const UNCLASSIFIED_NAME = 'Unclassified';
export const UNCLASSIFIED_COLOR = '#64748B';
/** D2: the Unclassified caller = the plain person (icon set v1.5 unknown/user.svg). */
export const UNCLASSIFIED_CALLER_ICON = 'User';
/** D6: the Unclassified request pill = the question mark. */
export const UNCLASSIFIED_CATEGORY_ICON = 'Help';
/** D10: values an old record carries that mean "nothing was classified" - the retired fallbacks. */
const RETIRED_FALLBACKS = new Set(['', 'general', 'uncategorized', 'unclassified', 'request']);

const norm = (v: string | null | undefined) => (v ?? '').trim().toLowerCase();

/** The list item a stored value means: key first, then name, then alias. Undefined = not in the list. */
export function resolveTaxonomy<T extends TaxonomyItem>(list: readonly T[], raw: string | null | undefined): T | undefined {
  const r = norm(raw);
  if (!r) return undefined;
  return list.find((x) => norm(x.key) === r)
    ?? list.find((x) => norm(x.name) === r)
    ?? list.find((x) => (x.aliases ?? []).some((a) => norm(a) === r));
}

/** True when the stored value says nothing was classified (blank, or a retired fallback such as "General"). */
export function isUnclassifiedValue(raw: string | null | undefined): boolean { return RETIRED_FALLBACKS.has(norm(raw)); }

/** Display facts for a caller type: the item, or the system Unclassified type. */
export function callerTypeDisplay(list: readonly TaxonomyItem[], raw: string | null | undefined): { key: string; name: string; color: string; icon: string; unclassified: boolean } {
  const hit = resolveTaxonomy(list, raw);
  if (hit) return { key: hit.key, name: hit.name, color: hit.color || UNCLASSIFIED_COLOR, icon: hit.icon || UNCLASSIFIED_CALLER_ICON, unclassified: false };
  return { key: UNCLASSIFIED_KEY, name: UNCLASSIFIED_NAME, color: UNCLASSIFIED_COLOR, icon: UNCLASSIFIED_CALLER_ICON, unclassified: true };
}

/** Display facts for a category (the workflow card). A value that matches nothing but is not a retired fallback keeps its own
 *  words on a slate pill (an old record whose card has since left the workflow); blank / General -> Unclassified. */
export function categoryDisplay(list: readonly TaxonomyItem[], raw: string | null | undefined): { key: string; name: string; color: string; icon: string; unclassified: boolean } {
  const hit = resolveTaxonomy(list, raw);
  if (hit) return { key: hit.key, name: hit.name, color: hit.color || UNCLASSIFIED_COLOR, icon: hit.icon || 'Tag', unclassified: false };
  if (!isUnclassifiedValue(raw)) return { key: (raw ?? '').trim(), name: (raw ?? '').trim(), color: UNCLASSIFIED_COLOR, icon: 'Tag', unclassified: false };
  return { key: UNCLASSIFIED_KEY, name: UNCLASSIFIED_NAME, color: UNCLASSIFIED_COLOR, icon: UNCLASSIFIED_CATEGORY_ICON, unclassified: true };
}

/** D5: the pill / drawer words - "Appointment · Schedule" (workflow card · request type). Unclassified never takes a type. */
export function requestWords(cat: { name: string; unclassified: boolean }, requestType: string | null | undefined): string {
  const t = (requestType ?? '').trim();
  if (cat.unclassified || !t || norm(t) === norm(cat.name) || norm(t) === 'general') return cat.name;
  const short = shortRequestType(cat.name, t);
  return `${cat.name} · ${short.charAt(0).toUpperCase()}${short.slice(1)}`;
}

/** v12.10.29 (#2237, Tushar 11/3 "will it shorten or remove only from second word, then i am ok"): the request type drops a word
 *  that repeats from the area ("Medical records" · "Records request" -> "Request"); the area is never shortened, and a type that
 *  would be left empty keeps its own words. */
export function shortRequestType(area: string, type: string): string {
  const areaWords = new Set(area.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean).flatMap((w) => [w, w.replace(/s$/, '')]));
  const kept = type.split(/\s+/).filter((w) => { const k = w.toLowerCase().replace(/[^\p{L}\p{N}]/gu, ''); return !(areaWords.has(k) || areaWords.has(k.replace(/s$/, ''))); });
  return kept.length > 0 ? kept.join(' ') : type;
}

/** The request type the call captured (request.type), from the record's Call data rows. */
export function requestTypeOf(fields: readonly { token: string; value: string }[] | null | undefined): string {
  return (fields ?? []).find((f) => f.token?.toLowerCase() === 'request.type' && (f.value ?? '').trim())?.value.trim() ?? '';
}
